import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AttemptState } from './types';
import { RootState } from '../../../app/store';
import { Attempt, AttemptsRetrieveRequest } from '../../../generated/api';
import { getAttemptsApiClient } from '../../../common/apiClients';
import { getMessageFromError } from '../../../common/utils/apiUtils';

const sliceName = 'attempts';

export const fetchApplicantAttemptAsync = createAsyncThunk<Attempt>(
  `${sliceName}/fetchApplicantAttempt`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAttemptsApiClient();
      return await apiClient.attemptsApplicantRetrieve();
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchAttemptsAsync = createAsyncThunk<Attempt[]>(
  `${sliceName}/fetchAttempts`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAttemptsApiClient();
      return await apiClient.attemptsList();
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createAttemptAsync = createAsyncThunk<Attempt>(
  `${sliceName}/createAttempt`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAttemptsApiClient();
      return await apiClient.attemptsCreate();
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const retrieveAttemptAsync = createAsyncThunk<Attempt, number>(
  `${sliceName}/retrieveAttempt`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAttemptsApiClient();
      const request: AttemptsRetrieveRequest = { id: arg };
      return await apiClient.attemptsRetrieve(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const initialState: AttemptState = {
  attempt: null,
  allAttempts: [],
  finished: false,
  loading: false
};

const attemptSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    clearAttempt: (state): AttemptState => {
      return { ...state, attempt: null, allAttempts: [], finished: false };
    },
    finishAttempt: (state): AttemptState => {
      return { ...state, finished: true };
    }
  },
  extraReducers: {
    [fetchApplicantAttemptAsync.pending.type]: (state): AttemptState => {
      return { ...state, loading: true };
    },
    [fetchApplicantAttemptAsync.rejected.type]: (state): AttemptState => {
      return { ...state, attempt: null, loading: false };
    },
    [fetchApplicantAttemptAsync.fulfilled.type]: (state, action: PayloadAction<Attempt>): AttemptState => {
      return { ...state, attempt: action.payload, loading: false };
    },
    [createAttemptAsync.pending.type]: (state): AttemptState => {
      return { ...state, loading: true };
    },
    [createAttemptAsync.rejected.type]: (state): AttemptState => {
      return { ...state, attempt: null, loading: false };
    },
    [createAttemptAsync.fulfilled.type]: (state, action: PayloadAction<Attempt>): AttemptState => {
      return { ...state, attempt: action.payload, loading: false };
    },
    [fetchAttemptsAsync.pending.type]: (state): AttemptState => {
      return { ...state, loading: true };
    },
    [fetchAttemptsAsync.rejected.type]: (state): AttemptState => {
      return { ...state, allAttempts: [], loading: false };
    },
    [fetchAttemptsAsync.fulfilled.type]: (state, action: PayloadAction<Attempt[]>): AttemptState => {
      return { ...state, allAttempts: action.payload, loading: false };
    },
    [retrieveAttemptAsync.pending.type]: (state): AttemptState => {
      return { ...state, loading: true };
    },
    [retrieveAttemptAsync.rejected.type]: (state): AttemptState => {
      return { ...state, attempt: null, loading: false };
    },
    [retrieveAttemptAsync.fulfilled.type]: (state, action: PayloadAction<Attempt>): AttemptState => {
      return { ...state, attempt: action.payload, loading: false };
    }
  }
});

export const selectAttempts = (state: RootState): AttemptState => state.attempts;
export const { clearAttempt, finishAttempt } = attemptSlice.actions;
export default attemptSlice.reducer;
